import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, vShow as _vShow, withDirectives as _withDirectives, unref as _unref, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "flex" }
const _hoisted_2 = {
  key: 0,
  for: "comment",
  class: "block text-sm font-medium mb-1 text-gray-700"
}
const _hoisted_3 = {
  key: 0,
  class: "text-sm text-gray-500 mt-3 mb-4"
}
const _hoisted_4 = { class: "relative" }
const _hoisted_5 = { class: "bg-white h-2 rounded-t-md" }
const _hoisted_6 = ["value", "placeholder", "rows", "readonly", "disabled"]
const _hoisted_7 = {
  key: 0,
  class: "absolute z-10 top-2.5 right-0 pr-3 flex items-center cursor-pointer"
}
const _hoisted_8 = { class: "bg-white h-2 rounded-b-md" }
const _hoisted_9 = {
  key: 1,
  class: "mt-2 text-sm text-red-600"
}
const _hoisted_10 = {
  key: 2,
  class: "flex w-full gap-x-4 mt-2"
}
const _hoisted_11 = { class: "text-sm text-gray-500" }
const _hoisted_12 = { class: "font-medium text-gray-700" }
const _hoisted_13 = { class: "text-sm text-gray-500" }
const _hoisted_14 = { class: "font-medium text-gray-700" }
const _hoisted_15 = { class: "text-sm text-gray-500" }
const _hoisted_16 = { class: "font-medium text-gray-400" }

import { withDefaults, computed, onMounted, watch, ref } from "vue"
import { ExclamationCircleIcon } from "@heroicons/vue/solid"
import { useAgencyStore } from "@/stores/agency"

interface Props {
    modelValue: string
    templateString?: string
    label?: string
    description?: string
    rows?: number
    placeholder?: string
    invalid?: boolean
    errorMessage?: string
    textAreaClasses?: string[]
    readonly?: boolean
    isCountAvailable?: boolean
    numberOfRecipients?: number
    required?: boolean
    disabled?: boolean
    rounded?: boolean
    borderless?: boolean
    roundedTop?: boolean
    roundedBottom?: boolean
    styleType?: "primary" | "onlyEmail" | "onlyPhone" | "emailAndPhone"
}


export default /*#__PURE__*/_defineComponent({
  __name: 'GgmsTextarea',
  props: {
    modelValue: { default: "" },
    templateString: { default: "" },
    label: { default: "" },
    description: {},
    rows: { default: 4 },
    placeholder: { default: "" },
    invalid: { type: Boolean },
    errorMessage: {},
    textAreaClasses: {},
    readonly: { type: Boolean, default: false },
    isCountAvailable: { type: Boolean, default: false },
    numberOfRecipients: { default: 1 },
    required: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    rounded: { type: Boolean, default: true },
    borderless: { type: Boolean, default: false },
    roundedTop: { type: Boolean, default: false },
    roundedBottom: { type: Boolean, default: false },
    styleType: { default: "primary" }
  },
  emits: ["update:modelValue", "disableValidationError", "smsCountChanged", "costExceeded"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const emit = __emit

const textarea = ref()

const props = __props

const agencyStore = useAgencyStore()

const smsCount = computed(() => calculateNumberOfMessages(props.modelValue))

const cost = computed(() => {
    const costPerSms = 1
    return smsCount.value * costPerSms
})

const remainingPoints = computed(() => agencyStore.agency.points?.balance)

const styleTypeToHeight = {
    onlyEmail: "h-[343px]",
    onlyPhone: "h-[221px]",
    emailAndPhone: "h-[282px]",
    primary: "",
}

const textAreaStyle = computed(() => [
    props.borderless ? "border-0 focus:border-0 focus:ring-transparent" : "",
    props.disabled ? "opacity-50 cursor-not-allowed" : "",
    props.invalid && !props.modelValue?.length
        ? "pr-10 border-red-300 placeholder-red-300 focus:ring-red-500 focus:border-red-500"
        : "placeholder-gray-400 focus:ring-primary-color-500 focus:border-primary-color-500 border-gray-300 ",
    props.readonly ? "opacity-50 focus:ring-transparent focus:border-gray-300 cursor-not-allowed" : "",
    props.rounded ? "rounded-md" : "",
    "shadow-sm block w-full ",
    props.textAreaClasses,
    styleTypeToHeight[props.styleType] || "",
])

const starStyle = computed(() => [props.invalid ? "text-red-600" : "text-gray-700", "pl-1"])

function onInput(event: Event) {
    if (event) {
        const target = event.target as HTMLTextAreaElement
        emit("update:modelValue", target.value)
        emit("smsCountChanged", smsCount.value)
    }
}
function calculateNumberOfMessages(textMessage: string) {
    if (!textMessage) {
        return 0
    }
    const MAX_UNICODE_CHARS_PER_MESSAGE = 70

    // Calculate the maximum length of the message based on the character set
    // eslint-disable-next-line no-control-regex
    const maxMessageLength = textMessage.match(/^[\u0000-\u007F]*$/)
        ? 160 // GSM 7-bit characters
        : MAX_UNICODE_CHARS_PER_MESSAGE // Unicode characters

    // Calculate the number of messages required to send the entire message
    if (textMessage.length > maxMessageLength) {
        // Split message into parts of 67 characters each
        return Math.ceil(textMessage.length / (maxMessageLength - 7))
    } else {
        return 1
    }
}

function disableValidationError() {
    emit("disableValidationError")
}

function onSelectInsertString(templateString: string) {
    const cursorPosition = textarea.value.selectionStart
    const textAreaValue = textarea.value.value
    const textBefore = textAreaValue.substring(0, cursorPosition)
    const textAfter = textAreaValue.substring(cursorPosition, textAreaValue.length)
    textarea.value.value = `${textBefore}${templateString}${textAfter}`
    textarea.value.focus()
    textarea.value.selectionStart = cursorPosition + templateString.length
    textarea.value.selectionEnd = cursorPosition + templateString.length
    emit("update:modelValue", textarea.value.value)
}

onMounted(() => {
    disableValidationError()
})

watch(
    () => props.templateString,
    (val) => {
        onSelectInsertString(val)
    }
)

watch(
    () => cost.value,
    () => {
        if (cost.value > remainingPoints.value) {
            emit("costExceeded", true)
        } else {
            emit("costExceeded", false)
        }
    }
)

__expose({
    textarea,
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("div", _hoisted_1, [
      (_ctx.label)
        ? (_openBlock(), _createElementBlock("label", _hoisted_2, _toDisplayString(_ctx.label), 1))
        : _createCommentVNode("", true),
      (_ctx.required)
        ? (_openBlock(), _createElementBlock("span", {
            key: 1,
            class: _normalizeClass(starStyle.value)
          }, "*", 2))
        : _createCommentVNode("", true)
    ]),
    (_ctx.description)
      ? (_openBlock(), _createElementBlock("p", _hoisted_3, _toDisplayString(_ctx.description), 1))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_4, [
      _withDirectives(_createElementVNode("div", _hoisted_5, null, 512), [
        [_vShow, _ctx.roundedTop]
      ]),
      _createElementVNode("textarea", {
        ref_key: "textarea",
        ref: textarea,
        value: _ctx.modelValue,
        placeholder: _ctx.placeholder,
        rows: _ctx.rows,
        class: _normalizeClass(textAreaStyle.value),
        readonly: _ctx.readonly,
        disabled: _ctx.disabled,
        onInput: onInput,
        onFocus: disableValidationError
      }, null, 42, _hoisted_6),
      (_ctx.invalid && !_ctx.modelValue)
        ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
            (_openBlock(), _createBlock(_resolveDynamicComponent(_unref(ExclamationCircleIcon)), {
              class: "h-5 w-5 text-red-600",
              "aria-hidden": "true"
            }))
          ]))
        : _createCommentVNode("", true),
      _withDirectives(_createElementVNode("div", _hoisted_8, null, 512), [
        [_vShow, _ctx.roundedBottom]
      ])
    ]),
    (_ctx.errorMessage)
      ? (_openBlock(), _createElementBlock("p", _hoisted_9, _toDisplayString(_ctx.errorMessage), 1))
      : _createCommentVNode("", true),
    (_ctx.isCountAvailable)
      ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
          _createElementVNode("p", _hoisted_11, [
            _createTextVNode(" Characters: "),
            _createElementVNode("span", _hoisted_12, _toDisplayString(_ctx.modelValue.length), 1)
          ]),
          _createElementVNode("p", _hoisted_13, [
            _createTextVNode(" SMS: "),
            _createElementVNode("span", _hoisted_14, _toDisplayString(smsCount.value), 1)
          ]),
          _createElementVNode("p", _hoisted_15, [
            _createTextVNode(" Cost: "),
            _createElementVNode("span", {
              class: _normalizeClass([cost.value > remainingPoints.value ? 'text-orange-500' : '', "font-medium text-gray-700"])
            }, _toDisplayString(cost.value), 3),
            _createElementVNode("span", _hoisted_16, "/" + _toDisplayString(remainingPoints.value), 1)
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})